/**
 * InBody results export: turns a member's inbody_scans rows into files they
 * can keep, paste into a wellness plan, or hand to an AI assistant.
 *
 *   inbodyScansToCsv      → every scan, every metric, one row per scan
 *                           (spreadsheets, Google Sheets, a coach's tracker)
 *   inbodySummaryMarkdown → the latest scan with healthy ranges, the change
 *                           since the previous scan and since the first, and
 *                           the segmental split, as plain markdown (docs,
 *                           Notion, ChatGPT / Claude projects)
 *
 * Used by BOTH the web app (src/redesign/inbodyExport.ts re-exports this file
 * for the download / copy buttons) and the email-inbody-results edge
 * function, so the file a member downloads and the one they get emailed can
 * never disagree. That is why it has no imports: it must run unchanged in
 * Vite and in Deno.
 *
 * Callers pass only the scans the member can see (hidden = false); hiding a
 * scan takes it out of the export exactly as it takes it out of the charts.
 *
 * Sits beside the PDF report (src/lib/inbodyReport). That one can't be
 * imported here — edge functions can't reach src/ — so the metric labels,
 * units, date style and file naming below are kept in step with
 * src/lib/inbodyReport/model.ts by hand. Change one, change both.
 */

/** Loose row shape: every column is optional so a partial select still works. */
export type InbodyExportRow = {
  scan_date: string;
  [column: string]: unknown;
};

type Field = { col: string; label: string; unit: string };

/** Column order for the CSV, and the metric table in the summary. Labels and
 *  units match the PDF report's (LABELS / UNITS in inbodyReport/model.ts). */
export const INBODY_EXPORT_FIELDS: Field[] = [
  { col: "inbody_score", label: "inbody score", unit: "/100" },
  { col: "weight", label: "weight", unit: "kg" },
  { col: "skeletal_muscle_mass", label: "skeletal muscle mass", unit: "kg" },
  { col: "body_fat_mass", label: "body fat mass", unit: "kg" },
  { col: "percent_body_fat", label: "body fat percentage", unit: "%" },
  { col: "bmi", label: "bmi", unit: "kg/m²" },
  { col: "total_body_water", label: "total body water", unit: "L" },
  { col: "protein", label: "protein", unit: "kg" },
  { col: "minerals", label: "minerals", unit: "kg" },
  { col: "visceral_fat_level", label: "visceral fat level", unit: "level" },
  { col: "basal_metabolic_rate", label: "basal metabolic rate", unit: "kcal" },
];

/** Healthy-range columns the scanner reports, keyed by the metric they bound. */
const NORMAL_RANGE: Record<string, [string, string]> = {
  weight: ["weight_normal_min", "weight_normal_max"],
  skeletal_muscle_mass: ["smm_normal_min", "smm_normal_max"],
  body_fat_mass: ["bfm_normal_min", "bfm_normal_max"],
  percent_body_fat: ["pbf_normal_min", "pbf_normal_max"],
  bmi: ["bmi_normal_min", "bmi_normal_max"],
  total_body_water: ["tbw_normal_min", "tbw_normal_max"],
  protein: ["protein_normal_min", "protein_normal_max"],
  minerals: ["minerals_normal_min", "minerals_normal_max"],
};

const SEGMENTS: [string, string][] = [
  ["left_arm", "left arm"],
  ["right_arm", "right arm"],
  ["trunk", "trunk"],
  ["left_leg", "left leg"],
  ["right_leg", "right leg"],
];

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Trim float noise (70.30000001 → 70.3) without forcing trailing zeros. */
const fmt = (n: number): string => String(Math.round(n * 100) / 100);

/** A signed change, e.g. "+1.2" / "-0.4" / "0". */
const signed = (n: number): string => {
  const r = Math.round(n * 100) / 100;
  return r > 0 ? `+${r}` : String(r);
};

const SAST = "Africa/Johannesburg";

/** A bare "2026-04-12" is a SAST calendar day (as in the PDF report), not
 *  UTC midnight; anything else parses as given. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const parseScanDate = (iso: string): Date =>
  new Date(DATE_ONLY.test(String(iso)) ? `${iso}T12:00:00+02:00` : iso);

/** "2026-04-12 09:14" in SAST ("2026-04-12" for a date-only value).
 *  Unparseable input comes back unchanged. */
export function sastStamp(iso: string): string {
  const d = parseScanDate(iso);
  if (isNaN(d.getTime())) return String(iso);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: SAST,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  const day = `${p.year}-${p.month}-${p.day}`;
  return DATE_ONLY.test(String(iso)) ? day : `${day} ${p.hour}:${p.minute}`;
}

/** "12 apr 2026" in SAST, for headings — the PDF report's date style. */
export function sastDay(iso: string): string {
  const d = parseScanDate(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d
    .toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: SAST,
    })
    .replace(/\./g, "")
    .toLowerCase();
}

/** Oldest first; rows with no parseable date are dropped. */
export function sortScans<T extends InbodyExportRow>(rows: T[]): T[] {
  return (rows || [])
    .filter((r) => r && !isNaN(parseScanDate(r.scan_date).getTime()))
    .slice()
    .sort(
      (a, b) =>
        parseScanDate(a.scan_date).getTime() -
        parseScanDate(b.scan_date).getTime(),
    );
}

/**
 * One CSV cell. Quotes anything with a comma, quote or newline, and defuses
 * spreadsheet formula injection: a cell starting with = + - @ (or a tab / CR)
 * is prefixed with an apostrophe so Excel and Sheets show it as text instead
 * of running it. Plain negative numbers are left alone so they stay numeric.
 */
export function csvCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Every scan as CSV. Header row is always present, even with no scans.
 * Starts with a UTF-8 byte-order mark so Excel reads it as UTF-8 (otherwise
 * the ² in kg/m² shows as "Â²"); the download and the email attachment both
 * get it from here.
 */
export function inbodyScansToCsv(rows: InbodyExportRow[]): string {
  const cols: { head: string; get: (r: InbodyExportRow) => unknown }[] = [
    { head: "scan date (sast)", get: (r) => sastStamp(r.scan_date) },
    ...INBODY_EXPORT_FIELDS.map((f) => ({
      head: `${f.label} (${f.unit})`,
      get: (r: InbodyExportRow) => num(r[f.col]),
    })),
    ...Object.entries(NORMAL_RANGE).flatMap(([col, [lo, hi]]) => {
      const f = INBODY_EXPORT_FIELDS.find((x) => x.col === col)!;
      return [
        {
          head: `${f.label} healthy min (${f.unit})`,
          get: (r: InbodyExportRow) => num(r[lo]),
        },
        {
          head: `${f.label} healthy max (${f.unit})`,
          get: (r: InbodyExportRow) => num(r[hi]),
        },
      ];
    }),
    ...(["lean", "fat"] as const).flatMap((kind) =>
      SEGMENTS.flatMap(([key, label]) => [
        {
          head: `${kind} mass ${label} (kg)`,
          get: (r: InbodyExportRow) => num(r[`segmental_${kind}_${key}`]),
        },
        {
          head: `${kind} mass ${label} (% of norm)`,
          get: (r: InbodyExportRow) =>
            num(r[`segmental_${kind}_${key}_percent`]),
        },
      ]),
    ),
  ];
  const lines = [cols.map((c) => csvCell(c.head)).join(",")];
  for (const r of sortScans(rows)) {
    lines.push(cols.map((c) => csvCell(c.get(r))).join(","));
  }
  // CRLF is what RFC 4180 and Excel expect.
  return "\uFEFF" + lines.join("\r\n") + "\r\n";
}

/** Markdown table cell: no pipes or newlines that would break the table. */
const mdCell = (s: string): string =>
  s.replace(/\|/g, "/").replace(/\s+/g, " ");

export type SummaryOptions = {
  /** Member's first name, for the heading. Optional. */
  name?: string | null;
  /** When the export was made. Defaults to now. */
  generatedAt?: Date;
};

/**
 * A plain-markdown report of the member's scans, written to be pasted as-is
 * into a doc or an AI chat: labelled units, healthy ranges beside each value,
 * and changes spelled out so nothing has to be read off a chart.
 */
export function inbodySummaryMarkdown(
  rows: InbodyExportRow[],
  opts: SummaryOptions = {},
): string {
  const scans = sortScans(rows);
  const generated = sastStamp((opts.generatedAt ?? new Date()).toISOString());
  const who = opts.name ? mdCell(String(opts.name).trim()).toLowerCase() : "";
  const out: string[] = [];
  out.push(`# ${who ? who + "'s " : ""}inbody results · ZUZU`);
  out.push("");
  out.push(
    `exported ${generated} sast · ${scans.length} scan${scans.length === 1 ? "" : "s"} on file`,
  );
  out.push("");

  if (!scans.length) {
    out.push(
      "no scans yet. results appear here after your first inbody scan at ZUZU.",
    );
    return out.join("\n") + "\n";
  }

  const latest = scans[scans.length - 1];
  const prev = scans.length > 1 ? scans[scans.length - 2] : null;
  const first = scans.length > 2 ? scans[0] : null;

  out.push(`## latest scan · ${sastDay(latest.scan_date)}`);
  out.push("");
  const head = ["metric", "value", "healthy range", "status"];
  if (prev) head.push(`change since ${sastDay(prev.scan_date)}`);
  if (first) head.push(`change since ${sastDay(first.scan_date)}`);
  out.push(`| ${head.join(" | ")} |`);
  out.push(`|${head.map(() => " --- ").join("|")}|`);
  for (const f of INBODY_EXPORT_FIELDS) {
    const v = num(latest[f.col]);
    if (v === null) continue;
    const unit = f.unit === "/100" ? "/100" : ` ${f.unit}`;
    const range = NORMAL_RANGE[f.col];
    const lo = range ? num(latest[range[0]]) : null;
    const hi = range ? num(latest[range[1]]) : null;
    const st = inbodyStatus(f.col, latest);
    const cells = [
      f.label,
      `${fmt(v)}${unit}`,
      lo !== null && hi !== null
        ? `${fmt(lo)} to ${fmt(hi)}`
        : st
          ? st.range.replace(/^healthy /, "")
          : "-",
      st ? st.word : "-",
    ];
    const delta = (other: InbodyExportRow) => {
      const o = num(other[f.col]);
      return o === null ? "-" : signed(v - o);
    };
    if (prev) cells.push(delta(prev));
    if (first) cells.push(delta(first));
    out.push(`| ${cells.map(mdCell).join(" | ")} |`);
  }
  out.push("");

  const segRows = SEGMENTS.map(([key, label]) => {
    const lk = num(latest[`segmental_lean_${key}`]);
    const lp = num(latest[`segmental_lean_${key}_percent`]);
    const fk = num(latest[`segmental_fat_${key}`]);
    const fp = num(latest[`segmental_fat_${key}_percent`]);
    return { label, lk, lp, fk, fp };
  }).filter((s) => [s.lk, s.lp, s.fk, s.fp].some((x) => x !== null));
  if (segRows.length) {
    out.push("## where it sits (segmental)");
    out.push("");
    out.push(
      "100% is the healthy norm for your height. lean above 100% means more muscle than the norm; fat above 100% means more fat than the norm.",
    );
    out.push("");
    out.push(
      "| segment | lean mass | lean % of norm | fat mass | fat % of norm |",
    );
    out.push("| --- | --- | --- | --- | --- |");
    const kg = (n: number | null) => (n === null ? "-" : `${fmt(n)} kg`);
    const pc = (n: number | null) => (n === null ? "-" : `${fmt(n)}%`);
    for (const s of segRows) {
      out.push(
        `| ${s.label} | ${kg(s.lk)} | ${pc(s.lp)} | ${kg(s.fk)} | ${pc(s.fp)} |`,
      );
    }
    out.push("");
  }

  if (scans.length > 1) {
    out.push("## scan history");
    out.push("");
    out.push(
      "| date (sast) | score | weight (kg) | muscle (kg) | body fat (kg) | body fat % |",
    );
    out.push("| --- | --- | --- | --- | --- | --- |");
    const c = (r: InbodyExportRow, col: string) => {
      const n = num(r[col]);
      return n === null ? "-" : fmt(n);
    };
    for (const r of scans.slice().reverse()) {
      out.push(
        `| ${sastDay(r.scan_date)} | ${c(r, "inbody_score")} | ${c(r, "weight")} | ${c(r, "skeletal_muscle_mass")} | ${c(r, "body_fat_mass")} | ${c(r, "percent_body_fat")} |`,
      );
    }
    out.push("");
  }

  out.push("## what these mean");
  out.push("");
  for (const [col, g] of Object.entries(INBODY_GUIDANCE)) {
    if (num(latest[col]) === null) continue;
    out.push(`- **${g.label}:** ${g.note}`);
  }
  out.push("");

  out.push("---");
  out.push("");
  out.push(
    "measured on an inbody body composition analyser at ZUZU. these are wellness readings, not a medical diagnosis. speak to a doctor or registered dietitian before making medical or diet changes based on them.",
  );
  return out.join("\n") + "\n";
}

/**
 * "zuzu-inbody-results-2026-10-04.csv" — dated by the latest scan (SAST), the
 * same way the PDF is named ("zuzu-inbody-report-2026-10-04.pdf"), so all the
 * files from one scan sit together. Falls back to today with no scans.
 */
export function inbodyExportFilename(
  ext: "csv" | "md",
  rows: InbodyExportRow[] = [],
  now = new Date(),
): string {
  const scans = sortScans(rows);
  const latest = scans[scans.length - 1];
  const day = sastStamp(latest ? latest.scan_date : now.toISOString()).slice(
    0,
    10,
  );
  return `zuzu-inbody-results-${day}.${ext}`;
}

const esc = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/**
 * The "email me my results" body: a short note plus the latest scan as an
 * HTML table. The full history travels as the attached CSV and markdown
 * summary, which are the parts meant to be saved or pasted elsewhere.
 * Every value is escaped; nothing here comes from the browser.
 */
export function inbodyResultsEmailHtml(
  rows: InbodyExportRow[],
  opts: { name?: string | null } = {},
): string {
  const scans = sortScans(rows);
  const latest = scans[scans.length - 1];
  const hi = opts.name
    ? `hi ${esc(String(opts.name).trim().toLowerCase())},`
    : "hi,";
  const tableRows = latest
    ? INBODY_EXPORT_FIELDS.map((f) => {
        const v = num(latest[f.col]);
        if (v === null) return "";
        const range = NORMAL_RANGE[f.col];
        const lo = range ? num(latest[range[0]]) : null;
        const hiR = range ? num(latest[range[1]]) : null;
        const unit = f.unit === "/100" ? "/100" : ` ${f.unit}`;
        return `<tr><td style="color:#676d88;padding:6px 14px 6px 0">${esc(f.label)}</td><td style="padding:6px 14px 6px 0"><b>${esc(fmt(v) + unit)}</b></td><td style="color:#676d88;padding:6px 0">${lo !== null && hiR !== null ? esc(`healthy ${fmt(lo)} to ${fmt(hiR)}`) : ""}</td></tr>`;
      }).join("")
    : "";
  return `<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;max-width:560px;margin:0 auto;color:#29261b;line-height:1.6">
    <h2 style="font-weight:600">your inbody results</h2>
    <p>${hi}</p>
    <p>here are your inbody results from ZUZU, as you asked. ${scans.length} scan${scans.length === 1 ? "" : "s"} on file${latest ? `, latest on ${esc(sastDay(latest.scan_date))}` : ""}.</p>
    ${tableRows ? `<table style="font-size:14px;border-collapse:collapse;margin:8px 0 18px">${tableRows}</table>` : ""}
    <p><b>attached:</b></p>
    <ul style="padding-left:18px">
      <li><b>summary (.md)</b> - paste it into a doc, your wellness plan, or an ai assistant like chatgpt or claude.</li>
      <li><b>all scans (.csv)</b> - opens in excel or google sheets, one row per scan.</li>
    </ul>
    <p style="color:#676d88;font-size:13px">these are wellness readings, not a medical diagnosis. you asked for this email from your ZUZU profile; if that wasn't you, reply and let us know.</p>
  </div>`;
}

/* ═══════════════ is this reading good or bad? ═══════════════
   A number and an up/down arrow don't tell a member whether they're fine.
   These rules turn a reading into a plain status against a healthy range,
   used by the result tiles in the app and by the exported summary.

   The range comes from the scanner's own norms for that member (the
   *_normal_min / *_normal_max columns, which InBody sets from their height,
   sex and age). Where the scanner sent none, a published default is used:
     - visceral fat: InBody's guideline, level 1-9 healthy, 10+ high
       (the scanner reports no per-member range for it)
     - bmi: 18.5-25 (WHO adult bands, as the InBody sheet uses)
     - body fat %: InBody's defaults, 10-20% men, 18-28% women
   Body water and protein have no safe generic default (they scale with body
   size), so without a scanner range they get no status, only the note.

   Known difference from the PDF report (inbodyInsight.interpretScan): for
   body fat % the report prefers the age-and-sex range (Gallagher) over the
   scanner's, and uses it as the fallback; this uses the scanner's range,
   then InBody's sex-only default. Left as is for now — it's a logic change,
   not a wording one. */

export type InbodyStatusTone = "good" | "normal" | "watch";
export type InbodyStatus = {
  /** "in healthy range" / "below healthy range" / "above healthy range", or
   *  "above typical range" where being above isn't a concern (body water,
   *  protein — more muscle). */
  word: string;
  tone: InbodyStatusTone;
  /** "healthy 32.4 to 39.6 L" / "healthy is under 10". */
  range: string;
};

type Guide = {
  label: string;
  unit: string;
  /** Scanner norm columns, if it reports them. */
  norm?: [string, string];
  /** What it means, in one or two plain sentences. */
  note: string;
  /** How to read a value above range (below is always "watch"). */
  high: InbodyStatusTone;
};

export const INBODY_GUIDANCE: Record<string, Guide> = {
  weight: {
    label: "weight",
    unit: "kg",
    norm: ["weight_normal_min", "weight_normal_max"],
    high: "watch",
    note: "compared with the healthy range for your height. what it's made of (muscle vs fat) matters more than the number itself.",
  },
  total_body_water: {
    label: "total body water",
    unit: "L",
    norm: ["tbw_normal_min", "tbw_normal_max"],
    high: "normal",
    note: "all the water in your body. in range means you're well hydrated for your size. low usually means dehydration or less muscle, since muscle holds a lot of water. small day-to-day swings are normal.",
  },
  protein: {
    label: "protein",
    unit: "kg",
    norm: ["protein_normal_min", "protein_normal_max"],
    high: "good",
    note: "mostly the protein in your muscle, so it tracks muscle mass. below range usually means less muscle than the norm for your size. strength training and enough protein in your diet bring it up.",
  },
  visceral_fat_level: {
    label: "visceral fat level",
    unit: "level",
    high: "watch",
    note: "fat stored deep around your organs. higher levels are linked with heart disease and type 2 diabetes, so it's a useful one to keep an eye on. inbody's guideline is under level 10. regular exercise, good sleep and diet bring it down, often before the scale moves.",
  },
  bmi: {
    label: "bmi",
    unit: "kg/m²",
    norm: ["bmi_normal_min", "bmi_normal_max"],
    high: "watch",
    note: "weight for your height. it can't tell muscle from fat, so read it alongside body fat %.",
  },
  percent_body_fat: {
    label: "body fat percentage",
    unit: "%",
    norm: ["pbf_normal_min", "pbf_normal_max"],
    high: "watch",
    note: "the share of your weight that is fat. a better guide than bmi, because it separates fat from muscle.",
  },
};

/** Published defaults for when the scanner sent no range (see above). */
function defaultRange(
  col: string,
  row: InbodyExportRow,
): [number, number] | null {
  if (col === "bmi") return [18.5, 25];
  if (col === "percent_body_fat") {
    const g = String(row.gender ?? "")
      .trim()
      .toLowerCase();
    if (g.startsWith("f")) return [18, 28];
    if (g.startsWith("m")) return [10, 20];
  }
  return null;
}

/**
 * Status for one metric on one scan (or period average), or null when there
 * is no reading or no range to judge it against.
 */
export function inbodyStatus(
  col: string,
  row: InbodyExportRow,
): InbodyStatus | null {
  const g = INBODY_GUIDANCE[col];
  const v = num(row?.[col]);
  if (!g || v === null) return null;

  if (col === "visceral_fat_level") {
    const lvl = Math.round(v);
    return lvl >= 10
      ? {
          word: "above healthy range",
          tone: "watch",
          range: "healthy is under 10",
        }
      : {
          word: "in healthy range",
          tone: "good",
          range: "healthy is under 10",
        };
  }

  let lo = g.norm ? num(row[g.norm[0]]) : null;
  let hi = g.norm ? num(row[g.norm[1]]) : null;
  if (lo === null || hi === null || hi <= lo) {
    const d = defaultRange(col, row);
    if (!d) return null;
    [lo, hi] = d;
  }
  const unit = g.unit === "%" ? "%" : ` ${g.unit}`;
  const range = `healthy ${fmt(lo)} to ${fmt(hi)}${unit}`;
  if (v < lo) return { word: "below healthy range", tone: "watch", range };
  if (v > hi)
    return {
      // Above range is only a concern where it's coloured as one; more body
      // water or protein (i.e. more muscle) is "above typical", not unhealthy.
      word: g.high === "watch" ? "above healthy range" : "above typical range",
      tone: g.high,
      range,
    };
  return { word: "in healthy range", tone: "good", range };
}
